import   React         ,
       { forwardRef    }  from 'react';

import   classNames       from 'classnames';

import { Check  ,
         X      ,
         UserX  ,
         Ban    }  from 'lucide-react';

import   styles           from './ChatModeration.module.scss';

import { ChatModMessage         ,
         ChatModerationLevelInfo } from '../../../dataTypes/moderationManagment/chat_types';

import { pct            ,
         riskColor      ,
         messageChips   ,
         highlightParts ,
         shortDuration  ,
         secondsSince   }  from './chatModerationUi';

/**
 * Un message à valider. Tout ce qu'il faut pour trancher en une seconde :
 * d'où il vient (couleur et nom de la chaîne), qui l'écrit et ses antécédents
 * du jour, depuis quand il attend, le score de l'IA et ce qui l'a fait tiquer.
 * Les liens et numéros sont surlignés : c'est souvent pour eux qu'il est là.
 */

interface Props {
  message      : ChatModMessage;
  channelName  : string;
  color        : string;
  level        : ChatModerationLevelInfo;
  now          : number;
  focused      : boolean;
  checked      : boolean;
  confirmBan   : boolean;
  onFocus      : () => void;
  onCheck      : () => void;
  onApprove    : () => void;
  onReject     : () => void;
  onKick       : () => void;
  onAskBan     : () => void;
  onConfirmBan : () => void;
  onCancelBan  : () => void;
}

const AUTHOR_STATE: Record<string, string> = { admin: 'Exclu', ban: 'Banni', user: 'Parti' };

const PendingCard = forwardRef<HTMLDivElement, Props>(({ message, channelName, color, level, now, focused, checked, confirmBan,
                                                         onFocus, onCheck, onApprove, onReject, onKick, onAskBan, onConfirmBan, onCancelBan }, ref) => {

  const wait  = secondsSince(message.creationDate, now);
  const score = message.aiScore;
  const tone  = riskColor(score, level.publish, level.reject);
  const chips = messageChips(message);

  return (
    <article ref={ref}
             className={classNames(styles.card, focused && styles.cardFocused)}
             style={{ ['--chan' as any]: color }}
             tabIndex={-1}
             onMouseDown={onFocus}
             aria-label={`Message de ${message.pseudo} sur ${channelName}`}>

      <button type="button" className={styles.cardCheck} onClick={onCheck}
              aria-pressed={checked} aria-label={checked ? 'Retirer de la sélection' : 'Ajouter à la sélection'}>
        <span className={classNames(styles.check, checked && styles.checkOn)} style={{ ['--chan' as any]: '#2563EB' }}>
          {checked && <Check size={12} strokeWidth={3} />}
        </span>
      </button>

      <div className={styles.cardHead}>
        <span className={styles.chanTag}><span className={styles.swatch} />{channelName}</span>
        <span className={styles.pseudo}>{message.pseudo}</span>
        {message.authorTotal24h > 1 && (
          <span className={classNames(styles.history, message.authorRejected24h > 0 && styles.historyBad)}
                title="Sur vos chaînes, ces dernières 24 h">
            {message.authorRejected24h > 0
              ? `${message.authorRejected24h} refusé${message.authorRejected24h > 1 ? 's' : ''} sur ${message.authorTotal24h} messages`
              : `${message.authorTotal24h} messages aujourd'hui`}
          </span>
        )}
        {message.authorState && <span className={styles.authorTag}>{AUTHOR_STATE[message.authorState]}</span>}
        <span className={classNames(styles.wait, wait >= 30 && styles.waitWarm, wait >= 90 && styles.waitHot)}
              title="Temps d'attente : le spectateur ne voit pas encore son message">
          attend depuis {shortDuration(wait)}
        </span>
      </div>

      <div className={styles.text}>
        {highlightParts(message.text).map((part, i) => part.mark ? <mark key={i}>{part.text}</mark> : <React.Fragment key={i}>{part.text}</React.Fragment>)}
      </div>

      <div className={styles.verdict}>
        {score === null
          ? <span className={styles.noAi}>Non noté par l'IA ({message.aiReason ?? 'indisponible'}) : à juger vous-même</span>
          : <span className={styles.meter} title="Score de risque de l'IA, de 0 (sûr) à 100">
              <span className={styles.meterTrack}><span style={{ width: `${Math.max(3, score * 100)}%`, background: tone }} /></span>
              <span className={styles.meterValue} style={{ color: tone }}>{pct(score)}</span>
            </span>}
        {chips.map(chip => (
          <span key={chip.key} className={classNames(styles.chip,
                                                     chip.kind === 'harm' && styles.chipHarm,
                                                     chip.kind === 'flag' && styles.chipFlag,
                                                     chip.kind === 'soft' && styles.chipSoft)}>
            {chip.label}
          </span>
        ))}
        {score !== null && chips.length === 0 && level.publish === null && <span className={styles.faint} style={{ fontSize: 13 }}>Rien à signaler (modération manuelle)</span>}
      </div>

      {confirmBan ? (
        <div className={styles.confirm} role="alertdialog" aria-label="Confirmer le bannissement">
          <span>Bannir <b>{message.pseudo}</b> de toutes vos chaînes ? Ses messages en attente seront refusés.</span>
          <span className={styles.spacer} />
          <button type="button" className={classNames(styles.btn, styles.btnReject)} onClick={onConfirmBan} autoFocus>
            <Ban size={15} /> Bannir
          </button>
          <button type="button" className={classNames(styles.btn, styles.btnGhost)} onClick={onCancelBan}>Annuler</button>
        </div>
      ) : (
        <div className={styles.actions}>
          <button type="button" className={classNames(styles.btn, styles.btnApprove)} onClick={onApprove}>
            <Check size={16} /> Publier <span className={styles.kbd}>V</span>
          </button>
          <button type="button" className={classNames(styles.btn, styles.btnReject)} onClick={onReject}>
            <X size={16} /> Refuser <span className={styles.kbd}>R</span>
          </button>
          <span className={styles.spacer} />
          <button type="button" className={classNames(styles.btn, styles.btnGhost)} onClick={onKick}
                  title="Le sortir du salon. Il peut revenir.">
            <UserX size={15} /> Exclure <span className={styles.kbd}>E</span>
          </button>
          <button type="button" className={classNames(styles.btn, styles.btnDanger)} onClick={onAskBan}
                  title="Lui interdire le chat de toutes vos chaînes">
            <Ban size={15} /> Bannir <span className={styles.kbd}>B</span>
          </button>
        </div>
      )}
    </article>
  );
});

export default PendingCard;
